import React, { useCallback, useState } from 'react';

import type { ToggleProps, ToggleSize } from './Toggle.types';

/**
 * Toggle/Switch component for binary on/off states.
 */
const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      checked: controlledChecked,
      defaultChecked = false,
      onChange,
      disabled = false,
      size = 'md',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      id,
      name,
      value,
      className = '',
      label,
      labelPosition = 'right',
      description,
    },
    ref
  ) => {
    // Support both controlled and uncontrolled usage
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    const isControlled = controlledChecked !== undefined;
    const isChecked = isControlled ? controlledChecked : internalChecked;

    const reactId = React.useId();
    const toggleId = id || `toggle-${reactId}`;

    const handleClick = useCallback(() => {
      if (disabled) return;

      const newChecked = !isChecked;

      if (!isControlled) {
        setInternalChecked(newChecked);
      }

      onChange?.(newChecked);
    }, [disabled, isChecked, isControlled, onChange]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      },
      [handleClick]
    );

    // Size configurations
    const sizeConfig: Record<
      ToggleSize,
      {
        track: string;
        thumb: string;
        thumbTranslate: string;
      }
    > = {
      sm: {
        track: 'h-5 w-9',
        thumb: 'h-4 w-4',
        thumbTranslate: isChecked ? 'translate-x-4' : 'translate-x-0',
      },
      md: {
        track: 'h-6 w-11',
        thumb: 'h-5 w-5',
        thumbTranslate: isChecked ? 'translate-x-5' : 'translate-x-0',
      },
      lg: {
        track: 'h-7 w-14',
        thumb: 'h-6 w-6',
        thumbTranslate: isChecked ? 'translate-x-7' : 'translate-x-0',
      },
    };

    const config = sizeConfig[size];

    // Track styling based on state
    const trackClasses = [
      'relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent',
      'transition-colors duration-normal ease-in-out',
      'focus:outline-none focus-visible:ring-state-focus',
      config.track,
      isChecked
        ? 'bg-primary-400 border-primary-400'
        : 'bg-contrast-600 border-stroke-default',
      disabled ? 'cursor-not-allowed opacity-50' : 'hover:opacity-90',
      className,
    ].join(' ');

    // Thumb styling
    const thumbClasses = [
      'pointer-events-none inline-block rounded-full bg-white-50 ring-0',
      'transition-transform duration-normal ease-in-out',
      config.thumb,
      config.thumbTranslate,
    ].join(' ');

    // Label text size based on toggle size
    const labelSizeClasses = {
      sm: 'text-label-sm',
      md: 'text-label-sm',
      lg: 'text-body-md',
    };

    const descriptionSizeClasses = {
      sm: 'text-label-xs',
      md: 'text-body-sm',
      lg: 'text-body-sm',
    };

    const toggle = (
      <button
        ref={ref}
        id={toggleId}
        type="button"
        role="switch"
        aria-checked={isChecked}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={trackClasses}
      >
        <span aria-hidden="true" className={thumbClasses} />
        {/* Hidden inputs for form submission */}
        {name && (
          <input
            type="hidden"
            name={name}
            value={isChecked ? value || 'on' : ''}
          />
        )}
      </button>
    );

    // If no label, return just the toggle
    if (!label) {
      return toggle;
    }

    // With label layout
    const labelContent = (
      <div className="flex flex-col">
        <label
          htmlFor={toggleId}
          className={`font-medium text-neutral-50 cursor-pointer ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          } ${labelSizeClasses[size]}`}
        >
          {label}
        </label>
        {description && (
          <span className={`text-neutral-400 ${descriptionSizeClasses[size]}`}>
            {description}
          </span>
        )}
      </div>
    );

    return (
      <div
        className={`flex items-center ${
          labelPosition === 'left' ? 'flex-row' : 'flex-row-reverse justify-end'
        } gap-3`}
      >
        {labelContent}
        {toggle}
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';

// Alias for Switch
const Switch = Toggle;
Switch.displayName = 'Switch';

export { Toggle, Switch };
export default Toggle;
